import { useEffect, useMemo, useRef, useState } from 'react';
import {
  activePath,
  addPath,
  deletePath,
  duplicatePath,
  fillPath,
  pathToSelection,
  renamePath,
  saveWorkPath,
  setActivePath,
  vectorMaskFromPath,
  WORK_PATH_NAME,
} from '../../state/vectorActions';
import { getActiveDocument, useAppStore } from '../../state/store';
import { pathBounds, pathToPolylines } from '../../vector/geometry';
import type { VectorPath } from '../../vector/types';
import { Icon } from '../components/controls';
import { Panel } from './Panel';

const THUMB_SIZE = 36;
const THUMB_PADDING = 4;

function PathThumbnail({ path }: { path: VectorPath }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const polylines = useMemo(() => pathToPolylines(path.subpaths), [path.subpaths]);
  const bounds = useMemo(() => pathBounds(path.subpaths), [path.subpaths]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scale = window.devicePixelRatio || 1;
    canvas.width = THUMB_SIZE * scale;
    canvas.height = THUMB_SIZE * scale;

    const context = canvas.getContext('2d');
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (!bounds) return;

    const width = Math.max(bounds.width, 1);
    const height = Math.max(bounds.height, 1);
    const fit = (THUMB_SIZE - THUMB_PADDING * 2) / Math.max(width, height);
    const offsetX = (THUMB_SIZE - bounds.width * fit) / 2 - bounds.x * fit;
    const offsetY = (THUMB_SIZE - bounds.height * fit) / 2 - bounds.y * fit;

    context.scale(scale, scale);
    context.strokeStyle = '#d7d7dc';
    context.lineWidth = 1;
    context.lineJoin = 'round';
    context.lineCap = 'round';

    polylines.forEach((polyline, index) => {
      if (polyline.length === 0) return;
      context.beginPath();
      context.moveTo(polyline[0].x * fit + offsetX, polyline[0].y * fit + offsetY);
      polyline.slice(1).forEach((point) => {
        context.lineTo(point.x * fit + offsetX, point.y * fit + offsetY);
      });
      if (path.subpaths[index].closed) context.closePath();
      context.stroke();
    });
  }, [bounds, path.subpaths, polylines]);

  return <canvas className="path__thumb" ref={canvasRef} />;
}

function PathRow({ path, active }: { path: VectorPath; active: boolean }) {
  const [renaming, setRenaming] = useState(false);

  return (
    <div
      className={`path${active ? ' path--active' : ''}`}
      onPointerDown={() => setActivePath(active ? null : path.id)}
    >
      <PathThumbnail path={path} />
      {renaming ? (
        <input
          className="layer__name-input"
          autoFocus
          defaultValue={path.name}
          onBlur={(event) => {
            setRenaming(false);
            const name = event.target.value.trim();
            if (name && name !== path.name) renamePath(path.id, name);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') setRenaming(false);
          }}
        />
      ) : (
        <span className="path__name" onDoubleClick={() => setRenaming(true)}>
          {path.name}
        </span>
      )}
    </div>
  );
}

export function PathsPanel() {
  const store = useAppStore();
  const doc = getActiveDocument(store);
  const selectedPath = doc ? activePath(doc) : null;
  const activeLayerId = doc?.activeLayerId ?? null;

  return (
    <Panel
      id="paths"
      title="Paths"
      actions={
        <>
          <button
            type="button"
            className="button button--icon"
            title="Fill path"
            disabled={!selectedPath}
            onClick={() => selectedPath && fillPath(selectedPath.id, false)}
          >
            <Icon size={14} path="M4 4h16v16H4zM7 7h10v10H7z" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Stroke path"
            disabled={!selectedPath}
            onClick={() => selectedPath && fillPath(selectedPath.id, true)}
          >
            <Icon size={14} path="M4 4h16v16H4zM7 7h10v10H7z" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Path to selection"
            disabled={!selectedPath}
            onClick={() => selectedPath && pathToSelection(selectedPath.id)}
          >
            <Icon size={14} path="M4 4h7v7H4zM13 13h7v7h-7zM11 11l2 2" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Vector mask from path"
            disabled={!selectedPath || !activeLayerId}
            onClick={() => activeLayerId && vectorMaskFromPath(activeLayerId)}
          >
            <Icon size={14} path="M4 4h16v16H4zM12 4v16" />
          </button>
          {selectedPath?.name === WORK_PATH_NAME && (
            <button
              type="button"
              className="button button--icon"
              title="Save path"
              onClick={saveWorkPath}
            >
              <Icon size={14} path="M5 4h12l3 3v13H4V4h1zM8 4v6h8V4M8 20v-6h8v6" />
            </button>
          )}
          <button
            type="button"
            className="button button--icon"
            title="New path"
            disabled={!doc}
            onClick={() => addPath()}
          >
            <Icon size={14} path="M12 5v14M5 12h14" />
          </button>
          <button
            type="button"
            className="button button--icon"
            title="Duplicate path"
            disabled={!selectedPath}
            onClick={() => selectedPath && duplicatePath(selectedPath.id)}
          >
            <Icon size={14} path="M9 9h11v11H9zM4 4h11v3M4 4v11h3" />
          </button>
          <button
            type="button"
            className="button button--icon button--danger"
            title="Delete path"
            disabled={!selectedPath}
            onClick={() => selectedPath && deletePath(selectedPath.id)}
          >
            <Icon size={14} path="M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13" />
          </button>
        </>
      }
    >
      <div className="path-list">
        {doc?.paths.map((path) => (
          <PathRow key={path.id} path={path} active={path.id === doc.activePathId} />
        ))}
      </div>
    </Panel>
  );
}
